"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveDeviceType = exports.DeviceTypeNames = exports.DeviceTypes = void 0;
/**
 * **The device types supported by the system**
 *
 * @description
 * *RSE — 2025 06 30:*
 *
 * `DeviceTypes` is problematic in that it is an `enum` (from day 0) and can't be added to by external projects (i.e. ventures)
 *
 * Other `enum`-like structures have avoided this since (2025 06), but still require generics and sleight of hand to make work
 *
 * For example:
 * The pattern of `OperationType` from DynamoDB Streams (`lws/node_modules/@aws-sdk/client-dynamodb-streams/dist-types/models/models_0.d.ts`)
 * may offer at least an improvement on the enum-like approach.
 *
 * @example
 * ```ts
 * export const DeviceTypes = {
 *   VesselSensor: 0x10,           // 16
 *   ElsysSensor: 0x12,            // 18
 *   SolenoidController: 0x17,     // 23
 *   Agritel: 0x1a,                // 26
 *   GPSMLC: 0x1b,                 // 27
 *   RadarDistance: 0x1c,          // 28
 *   TakeFlowMeter: 0x1d,          // 29
 *   AngelTel: 0x1e,               // 30
 *   GenericSensor: 0x20,          // 32
 *   EffluentTracker: 0x21,        // 33
 *   Pump: 0x22,                   // 34
 *   SoilMoisture: 0x23,           // 35
 * } as const;
 *
 * export type DeviceTypes = typeof DeviceTypes[keyof typeof DeviceTypes];
 * ```
 *
 * It seems likely that a direct replacement (using the above) will cause a lot of `import { DeviceTypes, type DeviceTypes }` changes
 * and probably typecheck errors, although a file may import two `DeviceTypes` refrences — provided one is a `type` and the other isn't
 */
var DeviceTypes;
(function (DeviceTypes) {
    /**
     * **VesselSensor** (`0x10`: 16)
     *
     * Shoule be called a `ManiTel`
     *
     * - `V1` devices are known as `BoatSecure`
     * - `V2` devices are known as `ManiTel`
     *
     * *https://docs.google.com/spreadsheets/d/1tS0bHgITcMuMU0V66kHvH7GzOEj_Yvrk/edit#gid=779257245*
     */
    DeviceTypes[DeviceTypes["VesselSensor"] = 16] = "VesselSensor";
    /**
     * ***ElsysSensor*** (`0x3e`: 62)
     *
     * *MHT: "Off-the-shelf unit from `Elsys`, used a long time ago for PIR, temperature, humidity, etc."*
     *
     * *[Elsys](https://elsys.se)*
     */
    DeviceTypes[DeviceTypes["ElsysSensor"] = 62] = "ElsysSensor";
    /**
     * **HiveSafe** (`0x16`: 22)
     *
     * Should be called a `TamperMon`
     *
     * *Previously known as a `HiveSafe`*
     */
    DeviceTypes[DeviceTypes["HiveSafe"] = 22] = "HiveSafe";
    /**
     * **ValveAct** (`0x17`: 23)
     *
     * *Previously known as a `SolenoidController` or `Irrigator` or `IrrigatorControl` or `Sprinkler`*
     *
     * *[V3 payload definition](https://docs.google.com/spreadsheets/d/1yU4JoctM5j-EEqOwBjwzmOISxGmCPQ399DgC1SeNl0M/edit?usp=sharing)*
     *
     * *[V3 manual](https://docs.google.com/document/d/1Ga2df2AE517Pky1OtpI9SehqQvjB-Yx9NXVIg519fLc/edit?tab=t.0#heading=h.ecba0rss932u)*
     *
     * *[SDA_SI Specific V020:]()*
     */
    DeviceTypes[DeviceTypes["ValveAct"] = 23] = "ValveAct";
    /**
     * **AgriTel** (`0x1a`: 26)
     *
     * - `V2` hardware, with a pressure sensor, known as `Agritel V2` or `Agritel` `TankSensor`
     *
     * *[Payload definition](https://docs.google.com/spreadsheets/d/14ZS35ePMKSWd56hi-eJTBK8PFzdf7yQu/edit?usp=sharing&ouid=107027932265294806298&rtpof=true&sd=true)*
     *
     * *[V1.2 manual](https://docs.google.com/document/d/1WCf0c6b-Zq_M5MQGOqiRs-yrughsOiqybm9GQf7J8AA/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["AgriTel"] = 26] = "AgriTel";
    /**
     * **RoveTel** (`0x1b`: 27)
     *
     * *Previously known as `MobileAssetTracker` or `AnimalMonitor` or `HorseMovement` or `HorseSafe` or `GPSMLC`*
     *
     * - `V1` hardware:
     *   - *[Firmware folder](https://drive.google.com/drive/folders/14WGMXEIRte3_4MQE4UCyKlrpKBBkOPVX?usp=drive_link)*
     *   - *[Version log](https://docs.google.com/spreadsheets/d/16uDW0zkHYBz_kGMe838qQgxLmnR-BzFrnmo6mt_UTlE/edit?usp=sharing)*
     *   - *[Payload definition v0.4](https://docs.google.com/spreadsheets/d/1hTyD54g7lACgIQRuEwXPDSVtTWNSUvXEK60KG45t-bM/edit?usp=sharing)*
     *   - *[Payload definition v0.5h](https://docs.google.com/spreadsheets/d/1x02g7RkN34sSzJoFgFqPBADgv6FID9vr6wtwsB4XSjk/edit?usp=sharing)*
     *   - *[Payload definition v0.6b](https://docs.google.com/spreadsheets/d/1i38KhQSUHjIEq8-gcd6rI9oJC-iTkU-h3NLlqWgseXE/edit?usp=sharing)*
     *   - *[Payload definition v0.7](https://docs.google.com/spreadsheets/d/1czNm-vQf8XynJbleyOw82sStlNCwlRXSttihlMMaxDI/edit?usp=sharing)*
     *   - *[Payload definition v0.8.1](https://docs.google.com/spreadsheets/d/14HGDmHvtWSxEvTx-Oz7mupzg4WxvUfKX28C8jfiBJiM/edit?usp=sharing)*
     *   - *[Payload definition v0.9](https://docs.google.com/spreadsheets/d/1dJabKHKuM9BljPnE2IemXxzA4wxzzjZbjMKyEv3Z8ok/edit?usp=sharing)*
     *   - *[Payload definition v0.14 ~ v0.17](https://docs.google.com/spreadsheets/d/1YJBncjEbZd-HNKe4l4PMazYuQh6UmfGjryQZlAjgGns/edit?usp=sharing)*
     *   - *[Payload definition v0.20 ~ v0.27](https://docs.google.com/spreadsheets/d/1b-hT1Y30RAV6aF6AL1gToPpncvq1RT6khq8b6r9gaWI/edit?usp=sharing)*
     *   - *[Payload definition v0.29](https://docs.google.com/spreadsheets/d/1bypLgTd2KHysseQhfYDbg5GrE8Sj7LAEWn37Ukb2L4A/edit?usp=sharing)*
     *   - *[Payload definition v0.30](https://docs.google.com/spreadsheets/d/1fypCZdPzuv-JkGiBP7veJfq6nBDjl592RZQfi2vH5Cg/edit?usp=sharing)*
     * - `V2` hardware:
     *   - *[Firmware folder](https://drive.google.com/drive/folders/1DkDoUY1QhqqR13axJcZGfakcjXP-6eOI?usp=drive_link)*
     *   - *[Version log](https://docs.google.com/spreadsheets/d/1BwqQXg937LK0KrnQU99ydvpxzpqKra5X45cdPT-jOe4/edit?usp=sharing)*
     *   - *[SDA_SI Specific V015:4](https://docs.google.com/spreadsheets/d/1JDeeQQ4-NFltEIeFri1isW5QOESDye9x7BqefAXJlRs/edit?usp=sharing)*
     *
     * Firmware versions which are known to exist:
     *
     * - `V1` hardware:
     *   - `0.5.1`
     *   - `0.6`
     *   - `0.7`
     *   - `0.8`
     *   - `0.9.1`
     *   - `0.9.5`
     *   - `0.9.6`
     *   - `0.9.7`
     *   - `0.10`
     *   - `0.11`
     *   - `0.12.1`
     *   - `0.13`
     *   - `0.14`
     *   - `0.15`
     *   - `0.16`
     *   - `0.17`
     *   - `0.18`
     *   - `0.19`
     *   - `0.22`
     *   - `0.25`
     *   - `0.26`
     *   - `0.27`
     *   - `0.28`
     *   - `0.29a`
     *   - `0.30`
     * - `V2` hardware:
     *   - `2.7` (`2.07`)
     *   - `2.8` (`2.08`)
     *   - `2.9` (`2.09`)
     *   - `2.10`
     *   - `2.11`
     *   - `2.12`
     *   - `2.13`
     *   - `2.14`
     *   - `2.15`
     *   - `2.16`
     *   - `2.19`
     *   - `2.20`
     *   - `2.21`
     *   - `2.22`
     *   - `2.24`
     */
    DeviceTypes[DeviceTypes["RoveTel"] = 27] = "RoveTel";
    /**
     * **RangeTel** (`0x1c`: 28)
     *
     * - `V2` hardware is known as `GrainTec`
     * - `V3` hardware is known as `RangeTel`, previously known as`RadarDistanceSensor` or `RadarDistance`
     *
     * *[V2 payload definition](https://docs.google.com/spreadsheets/d/1HhsMWtMdw5AQ4cXIHG03at20vNMyguhu3B4mEdDYrY4/edit?usp=sharing)*
     *
     * *[V3 payload definition](https://docs.google.com/spreadsheets/d/1NJG_QiTOiQYi2JivY-GcoY5jYUYOhr-bWmFYZG_pQ1s/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["RangeTel"] = 28] = "RangeTel";
    /**
     * **TakeFlowMeter** (`0x1d`: 29)
     *
     * - `V2` {@link AgriTel | `AgriTel` } hardware, with a flow meter sensor, known as the `PulseCounter` or `PulseLogger`
     *
     * *[Payload definition](https://docs.google.com/spreadsheets/d/1KkcvQfGzHrOywTaXoLMpVHwrtEnSbb_q/edit?usp=sharing&ouid=107027932265294806298&rtpof=true&sd=true)*
     */
    DeviceTypes[DeviceTypes["TakeFlowMeter"] = 29] = "TakeFlowMeter";
    /**
     * **AngleTel** (`0x1e`: 30)
     *
     * - {@link RoveTel | `RoveTel`} hardware
     *
     * *[Payload definition](https://docs.google.com/spreadsheets/d/10qxNe3f_Y4ZabTo23EM-Rj4gyeysThouB0t6F1yMNWI/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["AngleTel"] = 30] = "AngleTel";
    /**
     * ***GenericSensor*** (`0x20`: 32)
     *
     * ???
     *
     * *WXC: "sounds like an AgriTel"*
     */
    DeviceTypes[DeviceTypes["GenericSensor"] = 32] = "GenericSensor";
    /**
     * **EffluentTracker** (`0x21`: 33)
     *
     * Should be called a `PlacementTel`
     *
     * *Previously known as an `EffluentSpreaderTracker`*
     */
    DeviceTypes[DeviceTypes["EffluentTracker"] = 33] = "EffluentTracker";
    /**
     * **VibeTel** (`0x22`: 34)
     *
     * *Previously known as the `VibrationPumpMonitor`*
     *
     * - {@link RoveTel | `RoveTel` } hardware
     *
     * *[SDA_SI Specific Vibration Monitor](https://docs.google.com/spreadsheets/d/1ZCoZNfF19AwPDJWihhha2fR2r1PAnFXcAdBOtC7-_Qg/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["VibeTel"] = 34] = "VibeTel";
    /**
     * **SoilMosture** (`0x23`: 35)
     *
     * - `V3` {@link AgriTel | `AgriTel`} hardware, with a moisture sensor (`SDI-12` protocol, currently an `EnviroPro EP100G`)
     */
    DeviceTypes[DeviceTypes["SoilMoisture"] = 35] = "SoilMoisture";
    /**
     * **Isolation Valve** (`0x24`: 36)
     *
     * - `V3` {@link ValveAct | `ValveAct`} hardware, known as the `RemoteIsolationValve`, `Master Valve Controller`
     *
     * *[Payload definition](https://docs.google.com/spreadsheets/d/1DAs9jEQi69ufRulR8sSrDDZGcsyBfEr77Vqwmmd6A_0/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["IsolationValve"] = 36] = "IsolationValve";
    /**
     * **SenseTel** (`0x25`: 37)
     *
     * *Previously known as an `Agritel V3`*
     *
     * *[Payload definition](https://docs.google.com/spreadsheets/d/1AzObEPYDLZLqOYZ4LHqiAp7cbjjHYMch-0_c0jb8yfw/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["SenseTel"] = 37] = "SenseTel";
    /**
     * **PowerAct** (`0x26`: 38)
     *
     * *Previously known as a `Pump Controller / Monitor`*
     *
     * *[SDA_SI Specific Pump Controller/Monitor](https://docs.google.com/spreadsheets/d/1SxUH7TAtpiamfeh9IpI277XTnbC3uwoWR8U_9QoaB8g/edit?usp=sharing)*
     *
     * *[PowerAct manual](https://docs.google.com/document/d/1GavxWcwXyOp5-fwX2gvNf32i-OV-mUlEqoRz08S80nU/edit?usp=sharing)*
     */
    DeviceTypes[DeviceTypes["PowerAct"] = 38] = "PowerAct";
    /**
     * A third party device (not IoTV spec)
     * Support for this through the network data/device control process is WIP
     */
    DeviceTypes[DeviceTypes["ThirdParty"] = 255] = "ThirdParty";
})(DeviceTypes || (exports.DeviceTypes = DeviceTypes = {}));
/**
 * These are human-readable, LXC-marketable names for the devices
 *
 * The actual device headers are in {@link DeviceTypes | `DeviceTypes`}
 */
exports.DeviceTypeNames = {
    [DeviceTypes.VesselSensor]: 'ManiTel',
    [DeviceTypes.ElsysSensor]: 'Elsys',
    [DeviceTypes.HiveSafe]: 'TamperMon',
    [DeviceTypes.ValveAct]: 'ValveAct',
    [DeviceTypes.AgriTel]: 'AgriTel',
    [DeviceTypes.RoveTel]: 'RoveTel',
    [DeviceTypes.RangeTel]: 'RangeTel',
    [DeviceTypes.TakeFlowMeter]: 'Flow Meter',
    [DeviceTypes.AngleTel]: 'AngleTel',
    [DeviceTypes.GenericSensor]: 'Generic Sensor',
    [DeviceTypes.EffluentTracker]: 'PlacementTel',
    [DeviceTypes.VibeTel]: 'VibeTel',
    [DeviceTypes.SoilMoisture]: 'Soil Moisture Sensor',
    [DeviceTypes.IsolationValve]: 'Isolation Valve',
    [DeviceTypes.SenseTel]: 'SenseTel',
    [DeviceTypes.PowerAct]: 'PowerAct',
    [DeviceTypes.ThirdParty]: 'Third Party'
};
/** Resolves a payload string of hex bytes to a known device type (or `undefined` if not known) */
const resolveDeviceType = (prefixedString) => {
    let deviceTypeRes = undefined;
    if (prefixedString) {
        const prefix = parseInt(prefixedString.substring(0, 2), 16);
        const res = DeviceTypes[prefix];
        const toMap = Object.fromEntries(Object.entries(DeviceTypes).filter(([k, v]) => typeof v === 'number'));
        const resOfRes = toMap[res];
        if (res && resOfRes) {
            deviceTypeRes = {
                deviceTypeName: res,
                deviceType: resOfRes,
            };
        }
    }
    return deviceTypeRes;
};
exports.resolveDeviceType = resolveDeviceType;
